// Front/src/pages/SearchServiceCall.js

import React, { useState, useEffect } from 'react';
import { receive_specific_task_with_info } from '../priorityApi';
import '../style/MyTasks.css';
import { useTranslate } from '../hooks/useTranslate';
import { useLanguageContext } from '../context/LanguageContext';
import { ToastContainer, toast } from 'react-toastify';
import { Helmet } from "react-helmet-async";
import { get_client_from_webapi } from '../api';
import { useAuthContext } from '../hooks/useAuthContext';
import { useNavigate } from 'react-router-dom';


const SearchServiceCall = () => {
    const [serviceCallId, setServiceCallId] = useState('');
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedSubtasks, setExpandedSubtasks] = useState({});
    const { language } = useLanguageContext();
    const t = useTranslate();
    const { user } = useAuthContext();
    const userId = user?.id || null;
    const navigate = useNavigate();
    const [clientCheckResults, setClientCheckResults] = useState({});

    useEffect(() => {
      const lastId = localStorage.getItem("lastServiceCallId");
      if (lastId) {
          setServiceCallId(lastId);
  
          // Optionally run the search automatically:
          (async () => {
              setLoading(true);
              const response = await receive_specific_task_with_info(lastId);
              if (response && response?.data) {
                  setTask(response.data);
              } else {
                  toast.error(t.serviceCallDoesntExist, {
                      icon: false,
                      autoClose: 3000,
                  });
              }
              setLoading(false);
          })();
      }
    }, [t]);

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        setTask(null);

        // Save the last searched service call ID
        localStorage.setItem("lastServiceCallId", serviceCallId)

        const response = await receive_specific_task_with_info(serviceCallId);

        if (response && response?.data) {
            setTask(response.data);
        } else {
        toast.error(t.serviceCallDoesntExist, {
            icon: false,
            autoClose: 3000,
        });
        }

        setLoading(false);
    };

    const toggleSubtask = (subId) => {
        setExpandedSubtasks((prev) => ({
        ...prev,
        [subId]: !prev[subId],
        }));
    };

    const handleTestClient = async (custId, docno) => {
        try {
          const response = await get_client_from_webapi(userId, custId);
          if (response?.status === 200) {
            toast.success(t.clientExists, {
              icon: false,
              autoClose: 3000,
            });
            setClientCheckResults(prev => ({ ...prev, [docno]: true }));
          } else {
            toast.error(`${response?.error?.response?.data || t.unknownError}`, {
              icon: false,
              autoClose: 3000,
            });
            setClientCheckResults(prev => ({ ...prev, [docno]: false }));
          }
        } catch (error) {
          toast.error(t.clientApiCallFailed + error, {
            icon: false,
            autoClose: 3000,
          });
        }
    };
      

    return (
        <div className="my-tasks-container" dir={language === 'he' ? 'rtl' : 'ltr'}>
          <Helmet>
            <title>{t.searchServiceCall}</title>
          </Helmet>
    
          <h2>{t.searchServiceCall}</h2>
    
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              value={serviceCallId}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                const filtered = value.replace(/[^A-Z0-9]/g, '');
                setServiceCallId(filtered);
              }}
              placeholder={t.enterServiceCall}
              className="search-input"
            />
            <button type="submit" className="button-refresh-tasks" disabled={loading || !serviceCallId.trim()}>
              {loading ? t.loading : t.search}
            </button>
          </form>
    
          <ToastContainer
            position="top-center"
            autoClose={3000}
            toastClassName="custom-toast"
            closeButton={true}
          />
    
          {task && (
            <div className="task-card expanded result-task" style={{ marginTop: '2rem' }}>
              <div className="task-client-row">
                <strong>{t.clientNumber}</strong>
                <span>{task.CUSTNAME}</span>
    
                <button
                  onClick={() => handleTestClient(task.CUSTNAME, task.DOCNO)}
                  className="button-check-client"
                >
                  {t.checkClient}
                </button>
    
                {clientCheckResults[task.DOCNO] === false && (
                  <button
                    onClick={() => navigate("/newclient", {
                      state: {
                        custId: task.CUSTNAME,
                        CDES: task.CDES,
                        ZMOZ_ADDRESS: task.ZMOZ_ADDRESS,
                        ZMOZ_EMAIL: task.ZMOZ_EMAIL,
                        PHONENUM: task.PHONENUM,
                        NAME: task.NAME,
                        DOCNO: task.DOCNO
                      }
                    })}
                    className="button-create-client"
                  >
                    {t.createClient}
                  </button>
                )}
              </div>
    
              <strong>{t.clientName}</strong> {task.CDES}<br />
              <strong>{t.site}</strong> {task.CODEDES}<br />
              <strong>{t.start}</strong> {new Date(task.PDATE).toLocaleDateString('en-GB')}<br />
              <strong>{t.contact}</strong> {task.NAME} ({task.PHONENUM})<br />
              <strong>{t.status}</strong> {task.CALLSTATUSCODE}<br />
              <strong>{t.comments}</strong> {task.DETAILS || "—"}<br />
              <strong>{t.address}</strong> {task.ZMOZ_ADDRESS}<br />
              <strong>{t.email}</strong> {task.ZMOZ_EMAIL}<br />
              <strong>{t.totalSubtasks}</strong> {task.TotalSubtasks}
    
              {task.SubTasks?.length > 0 ? (
                <div dir={language === 'he' ? 'rtl' : 'ltr'}>
                  <strong className="section-heading">{t.subTasks}</strong>
                  <ul className="subtasks-list">
                    {task.SubTasks.map((sub, idx) => {
                      const subId = `${task.DOCNO}_${sub.SERNUM || idx}`;
                      return (
                        <li
                          key={idx}
                          className={`subtask-item 
                            ${sub.APIFLAG === null ? 'subtask-api-flag-true' : ''}
                            ${sub.APIFLAG !== null && sub.IsInDocnet ? 'subtask-purple' : ''}
                          `}
                          onClick={() => toggleSubtask(subId)}
                        >
                          <div className={`task-card subtask-header ${expandedSubtasks[subId] ? "expanded" : ""}`}>
                            <span className="subtask-arrow">
                              {expandedSubtasks[subId] ? "🔽" : t.arrow}
                            </span>
                            <strong>{sub.PARTDES || "—"}</strong>&nbsp;—&nbsp;{sub.SERNUM}
                          </div>
                          {expandedSubtasks[subId] && (
                            <div className="subtask-details">
                              <div><strong>{t.makat}</strong> {sub.Makat}</div>
                              <div><strong>{t.serial}</strong> {sub.SERNUM}</div>
                              <div><strong>{t.itemCode}</strong> {sub.PARTNAME}</div>
                              <div><strong>{t.itemDesc}</strong> {sub.PARTDES}</div>
                              <div><strong>{t.comments}</strong> {sub.REMARKS || "—"}</div>
                              <div><strong>{t.internalId}</strong> {sub.SERN}</div>
                              <div><strong>{t.lineNumber}</strong> {sub.Kline}</div>
                              <div><strong>{t.existsInPriority}</strong> {sub.APIFLAG === null ? t.yes : t.no}</div>
                              <div><strong>{t.existsInDocnet}</strong> {sub.IsInDocnet ? t.yes : t.no}</div>
                              <div><strong>{t.symboled}</strong> {sub.Makat?.toString().endsWith("-1") ? "Yes" : "No"}</div>
    
                              <div className="button-subtask-wrapper">
                                <button
                                  className="button-subtask-report"
                                  onClick={() =>
                                    navigate("/newreport", {
                                      state: {
                                        PDATE: task.PDATE,
                                        fromSearch: true,
                                        DOCNO: task.DOCNO,
                                        AGENTCODE: task.AGENTCODE,
                                        MAKAT: sub.Makat,
                                        itemCode: sub.PARTNAME,
                                        KLINE: sub.Kline,
                                        SERNUM: sub.SERNUM,
                                        SERN: sub.SERN,
                                        custId: task.CUSTNAME,
                                        address: task.ZMOZ_ADDRESS,
                                        SAMLIL: sub.Makat?.toString().endsWith("-1") ? "Yes" : "No"
                                      }
                                    })
                                  }
                                >
                                  {t.newReport}
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <div>{t.noSubTasks}</div>
              )}
            </div>
          )}
        </div>
      );
    };
    
    export default SearchServiceCall;